import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, CalendarClock, CheckCircle2, FileText, ThumbsDown, ThumbsUp } from "lucide-react";
import { toast } from "sonner";

import { API, api, apiError, fmtDate, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  EmptyState, Field, KpiCard, PageHeader, StatusBadge, TableSkeleton,
} from "@/components/common/Primitives";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TextAreaField } from "@/components/forms/Fields";
import { InterviewForm } from "@/components/forms/OpsForms";

export function ClientPortal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState(null);
  const [activeJob, setActiveJob] = useState(null);
  const [board, setBoard] = useState(null);
  const [ivOpen, setIvOpen] = useState(false);
  const [feedback, setFeedback] = useState({ open: false, card: null, text: "" });
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    api.get("/jobs", { params: { limit: 50 } })
      .then((r) => {
        setJobs(r.data.items);
        setActiveJob(r.data.items[0] || null);
      })
      .catch((e) => {
        toast.error(apiError(e));
        setJobs([]);
      });
  }, []);

  useEffect(() => {
    if (!activeJob) return;
    setBoard(null);
    api.get(`/jobs/${activeJob.id}/pipeline`).then((r) => setBoard(r.data)).catch(() => setBoard(null));
  }, [activeJob, refresh]);

  const act = async (card, stage) => {
    try {
      await api.post(`/jobs/${activeJob.id}/pipeline/move`, { application_ids: [card.application_id], stage });
      toast.success(stage === "Rejected" ? "Candidate rejected" : `Moved to ${stage}`);
      setRefresh((r) => r + 1);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const submitFeedback = async () => {
    try {
      await api.post("/communications", {
        candidate_id: feedback.card.candidate_id, job_id: activeJob.id, channel: "Note",
        subject: `Client feedback · ${activeJob.title}`, body: feedback.text,
      });
      toast.success("Feedback shared with the recruitment team");
      setFeedback({ open: false, card: null, text: "" });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const submitted = board
    ? Object.entries(board.stages).flatMap(([stage, cards]) => cards.map((c) => ({ ...c, stage })))
    : [];

  return (
    <div className="space-y-6">
      <PageHeader
        meta="Client portal"
        title={`Welcome, ${user?.name?.split(" ")[0]}`}
        subtitle="Review submitted profiles, request interviews and share feedback. You only ever see your own organisation's data."
      />

      {!jobs ? (
        <TableSkeleton rows={4} cols={3} />
      ) : jobs.length === 0 ? (
        <EmptyState icon={Briefcase} title="No open requirements" description="Your DIGNIFYD account manager will raise requirements here." />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <KpiCard label="Open requirements" value={jobs.filter((j) => j.status === "Open").length} icon={Briefcase} testid="portal-kpi-jobs" />
            <KpiCard label="Profiles submitted" value={submitted.length} icon={FileText} tone="amber" />
            <KpiCard label="In interview" value={submitted.filter((s) => s.stage.startsWith("Interview") || s.stage === "Final Interview").length} icon={CalendarClock} tone="violet" />
            <KpiCard label="Selected" value={submitted.filter((s) => ["Selected", "Offer", "Offer Accepted", "Joined"].includes(s.stage)).length} icon={CheckCircle2} tone="emerald" />
          </div>

          <div className="flex flex-wrap gap-2">
            {jobs.map((j) => (
              <button
                key={j.id}
                onClick={() => setActiveJob(j)}
                data-testid="portal-job-chip"
                className={`rounded-full border px-3.5 py-1.5 text-[11px] transition-colors ${
                  activeJob?.id === j.id
                    ? "border-blue-400 bg-blue-50 text-blue-700"
                    : "border-slate-200 bg-white text-slate-500 hover:border-blue-300"
                }`}
              >
                {j.title} · <span className="font-mono-num">{j.applicant_count}</span>
              </button>
            ))}
          </div>

          {activeJob && (
            <section className="surface rounded-md p-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="label-xs">{activeJob.job_code}</p>
                  <h2 className="mt-1 text-lg font-medium text-slate-900">{activeJob.title}</h2>
                  <p className="mt-1 text-xs text-slate-500">
                    {activeJob.location} · {activeJob.min_experience ?? "?"}–{activeJob.max_experience ?? "?"} yrs ·{" "}
                    {activeJob.openings} opening{activeJob.openings > 1 ? "s" : ""}
                  </p>
                </div>
                <Button onClick={() => setIvOpen(true)} data-testid="portal-schedule-interview" className="h-9 gap-2 rounded-full bg-blue-600 px-4 text-xs hover:bg-blue-500">
                  <CalendarClock className="h-3.5 w-3.5" /> Schedule interview
                </Button>
              </div>

              {!board ? (
                <TableSkeleton rows={4} cols={4} />
              ) : submitted.length === 0 ? (
                <EmptyState icon={FileText} title="No profiles submitted yet" description="Shortlisted candidates will appear here for your review." />
              ) : (
                <div className="mt-6 space-y-3">
                  {submitted.map((c) => (
                    <article key={c.application_id} className="rounded border border-slate-200 bg-slate-50 p-4" data-testid="portal-candidate-card">
                      <div className="flex flex-wrap items-start justify-between gap-4">
                        <div className="min-w-0">
                          <p className="text-sm text-slate-900">{c.name}</p>
                          <p className="text-xs text-slate-500">{c.designation || "Role not available"}</p>
                          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-400">
                            <span className="font-mono-num">{c.experience ?? "?"} yrs</span>
                            <span>{c.location || "Location not available"}</span>
                            <span>Notice: {c.notice_period || "Not available"}</span>
                          </div>
                          <div className="mt-2 flex flex-wrap gap-1">
                            {(c.skills || []).map((s) => (
                              <span key={s} className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">{s}</span>
                            ))}
                          </div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <StatusBadge value={c.stage} />
                          {c.match_score != null && (
                            <span className="font-mono-num text-xs text-slate-500">{c.match_score}% match</span>
                          )}
                        </div>
                      </div>
                      <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-200 pt-3">
                        <Button size="sm" variant="ghost" onClick={() => act(c, "Client Review")} data-testid="portal-shortlist-button"
                          className="h-8 gap-1.5 rounded-full border border-slate-200 text-[11px] text-slate-600 hover:bg-slate-100">
                          <ThumbsUp className="h-3 w-3" /> Shortlist
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => act(c, "Interview 1")} data-testid="portal-request-interview"
                          className="h-8 gap-1.5 rounded-full border border-slate-200 text-[11px] text-slate-600 hover:bg-slate-100">
                          <CalendarClock className="h-3 w-3" /> Request interview
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setFeedback({ open: true, card: c, text: "" })} data-testid="portal-feedback-button"
                          className="h-8 rounded-full border border-slate-200 text-[11px] text-slate-600 hover:bg-slate-100">
                          Add feedback
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => act(c, "Rejected")} data-testid="portal-reject-button"
                          className="h-8 gap-1.5 rounded-full border border-rose-200 text-[11px] text-rose-700 hover:bg-rose-50">
                          <ThumbsDown className="h-3 w-3" /> Reject
                        </Button>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          )}
        </>
      )}

      <InterviewForm open={ivOpen} onOpenChange={setIvOpen} defaults={{ job_id: activeJob?.id }} onSaved={() => setRefresh((r) => r + 1)} />

      <Dialog open={feedback.open} onOpenChange={(v) => !v && setFeedback({ open: false, card: null, text: "" })}>
        <DialogContent className="max-w-lg border-slate-200 bg-white">
          <DialogHeader>
            <DialogTitle className="text-base text-slate-900">Feedback on {feedback.card?.name}</DialogTitle>
          </DialogHeader>
          <TextAreaField label="Your feedback" value={feedback.text} onChange={(v) => setFeedback((f) => ({ ...f, text: v }))} rows={6} testid="portal-feedback-input" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setFeedback({ open: false, card: null, text: "" })} className="text-slate-500">Cancel</Button>
            <Button onClick={submitFeedback} data-testid="portal-feedback-submit" className="rounded-full bg-blue-600 px-5 hover:bg-blue-500">Share feedback</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function CandidatePortal() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);

  useEffect(() => {
    if (!user?.candidate_id) return;
    api.get(`/candidates/${user.candidate_id}`).then((r) => setMe(r.data)).catch((e) => toast.error(apiError(e)));
  }, [user]);

  if (!user?.candidate_id)
    return <EmptyState title="No candidate profile linked" description="Ask your recruiter to link your portal account to your profile." />;
  if (!me) return <TableSkeleton rows={6} cols={3} />;

  return (
    <div className="space-y-6">
      <PageHeader
        meta="Candidate portal"
        title={`Hello, ${me.full_name.split(" ")[0]}`}
        subtitle="Your profile, applications, interviews and offers with DIGNIFYD Talent Connect."
      />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <KpiCard label="Applications" value={me.applications?.length || 0} icon={Briefcase} testid="candidate-kpi-apps" />
        <KpiCard label="Interviews" value={me.interviews?.length || 0} icon={CalendarClock} tone="violet" />
        <KpiCard label="Offers" value={me.offers?.length || 0} icon={CheckCircle2} tone="emerald" />
        <KpiCard label="Documents" value={me.documents?.length || 0} icon={FileText} tone="slate" />
      </div>

      <Tabs defaultValue="applications">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 border border-slate-200 bg-white p-1">
          {["applications", "interviews", "offers", "profile", "documents"].map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`candidate-portal-tab-${t}`}
              className="rounded px-4 py-1.5 text-xs capitalize data-[state=active]:bg-blue-100 data-[state=active]:text-blue-700">
              {t}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="applications" className="mt-5">
          {(me.applications || []).length === 0 ? (
            <EmptyState icon={Briefcase} title="No applications yet" />
          ) : (
            <div className="surface divide-y divide-slate-200 rounded-md">
              {me.applications.map((a) => (
                <div key={a.id} className="flex items-center justify-between gap-4 p-4" data-testid="portal-application-row">
                  <div>
                    <p className="text-sm text-slate-900">{a.job_title}</p>
                    <p className="text-xs text-slate-500">{a.client_name || "Client confidential"}</p>
                  </div>
                  <StatusBadge value={a.stage} />
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="interviews" className="mt-5">
          {(me.interviews || []).length === 0 ? (
            <EmptyState icon={CalendarClock} title="No interviews scheduled" />
          ) : (
            <div className="surface divide-y divide-slate-200 rounded-md">
              {me.interviews.map((iv) => (
                <div key={iv.id} className="flex items-center justify-between gap-4 p-4">
                  <div>
                    <p className="text-sm text-slate-900">{iv.round_name} · {iv.interview_type}</p>
                    <p className="font-mono-num text-xs text-slate-500">{fmtDateTime(iv.scheduled_at)}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusBadge value={iv.status} />
                    {iv.meeting_link && (
                      <Button size="sm" variant="outline" onClick={() => window.open(iv.meeting_link, "_blank")}
                        className="h-8 rounded-full border-slate-200 bg-transparent text-xs text-slate-600">
                        Join
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="offers" className="mt-5">
          {(me.offers || []).length === 0 ? (
            <EmptyState icon={CheckCircle2} title="No offers yet" />
          ) : (
            <div className="surface divide-y divide-slate-200 rounded-md">
              {me.offers.map((o) => (
                <div key={o.id} className="flex items-center justify-between gap-4 p-4">
                  <div>
                    <p className="text-sm text-slate-900">{o.job_title}</p>
                    <p className="font-mono-num text-xs text-slate-500">{o.offered_ctc} LPA · joining {fmtDate(o.joining_date)}</p>
                  </div>
                  <div className="space-y-1.5 text-right">
                    <StatusBadge value={o.status} />
                    {o.joining_status && <StatusBadge value={o.joining_status} />}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="profile" className="mt-5">
          <div className="surface grid gap-5 rounded-md p-6 sm:grid-cols-3">
            <Field label="Designation" value={me.current_designation} />
            <Field label="Company" value={me.current_company} />
            <Field label="Experience" value={me.total_experience_years ? `${me.total_experience_years} yrs` : null} mono />
            <Field label="Location" value={me.location} />
            <Field label="Expected CTC" value={me.expected_ctc} mono />
            <Field label="Notice period" value={me.notice_period} />
            <div className="sm:col-span-3">
              <p className="label-xs mb-2">Skills</p>
              <div className="flex flex-wrap gap-1.5">
                {(me.skills || []).map((s) => (
                  <span key={s} className="rounded border border-slate-200 bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{s}</span>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="documents" className="mt-5">
          {(me.documents || []).length === 0 ? (
            <EmptyState icon={FileText} title="No documents on file" />
          ) : (
            <div className="surface divide-y divide-slate-200 rounded-md">
              {me.documents.map((d) => (
                <div key={d.id} className="flex items-center justify-between gap-4 p-4">
                  <div>
                    <p className="text-sm text-slate-800">{d.name}</p>
                    <p className="text-[11px] text-slate-400">{d.doc_type} · {fmtDate(d.created_at)}</p>
                  </div>
                  {d.storage_path && (
                    <Button size="sm" variant="outline" onClick={() => window.open(`${API}/documents/${d.id}/download`, "_blank")}
                      className="h-8 rounded-full border-slate-200 bg-transparent text-xs text-slate-600">
                      Download
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
